import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api, type ApiResponse } from "@/api/client";
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Link from "@tiptap/extension-link";
import TextAlign from "@tiptap/extension-text-align";
import Underline from "@tiptap/extension-underline";
import Placeholder from "@tiptap/extension-placeholder";
import {
  ArrowLeft,
  Save,
  Loader2,
  Eye,
  Code,
  Type,
  Send,
  Sparkles,
  History,
  ChevronDown,
  Bold,
  Italic,
  Underline as UnderlineIcon,
  AlignLeft,
  AlignCenter,
  AlignRight,
  List,
  ListOrdered,
  Link as LinkIcon,
  Undo,
  Redo,
  Variable,
  Globe,
} from "lucide-react";
import toast from "react-hot-toast";
import clsx from "clsx";

interface Template {
  id: string;
  slug: string;
  name: string;
  description?: string;
  type: "email" | "pdf";
  category: string;
  typeLabel: string;
  categoryLabel: string;
  subject?: string;
  subjectSk?: string;
  content: string;
  contentSk?: string;
  variables?: string;
  isSystem: boolean;
  isActive: boolean;
  versionCount?: number;
}

interface TemplateVersion {
  id: string;
  templateId: string;
  version: number;
  content: string;
  contentSk?: string;
  subject?: string;
  subjectSk?: string;
  changeNote?: string;
  createdBy?: string;
  createdAt: string;
}

interface VariableInfo {
  key: string;
  label: string;
  description: string;
}

const CATEGORIES = [
  { value: "newsletter", label: "Newsletter" },
  { value: "booking", label: "Potvrzení rezervace" },
  { value: "payment", label: "Platby" },
  { value: "contract", label: "Smlouvy" },
  { value: "invoice", label: "Faktury" },
  { value: "voucher", label: "Vouchery" },
  { value: "other", label: "Ostatní" },
];

const TYPES = [
  { value: "email", label: "Email" },
  { value: "pdf", label: "PDF dokument" },
];

export default function TemplateEditPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const isNew = !id || id === "new";

  // Form state
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [type, setType] = useState<"email" | "pdf">("email");
  const [category, setCategory] = useState("newsletter");
  const [subject, setSubject] = useState("");
  const [subjectSk, setSubjectSk] = useState("");
  const [content, setContent] = useState("");
  const [contentSk, setContentSk] = useState("");
  const [isActive, setIsActive] = useState(true);

  // UI state
  const [editorMode, setEditorMode] = useState<"wysiwyg" | "html">("wysiwyg");
  const [language, setLanguage] = useState<"cs" | "sk">("cs");
  const [showPreview, setShowPreview] = useState(true);
  const [showVariables, setShowVariables] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [showAiModal, setShowAiModal] = useState(false);
  const [previewHtml, setPreviewHtml] = useState("");

  // Fetch template
  const { data: templateData, isLoading } = useQuery({
    queryKey: ["template", id],
    queryFn: () => api.get<ApiResponse<Template>>(`/api/admin/templates/${id}`),
    enabled: !isNew,
  });

  // Fetch variables
  const { data: variablesData } = useQuery({
    queryKey: ["template-variables"],
    queryFn: () =>
      api.get<ApiResponse<Record<string, VariableInfo[]>>>("/api/admin/templates/variables"),
  });

  const variables = variablesData?.data || {};

  // Initialize form from template
  useEffect(() => {
    if (templateData?.data) {
      const t = templateData.data;
      setName(t.name);
      setDescription(t.description || "");
      setType(t.type);
      setCategory(t.category);
      setSubject(t.subject || "");
      setSubjectSk(t.subjectSk || "");
      setContent(t.content);
      setContentSk(t.contentSk || "");
      setIsActive(t.isActive);
    }
  }, [templateData]);

  // TipTap editor
  const editor = useEditor({
    extensions: [
      StarterKit,
      Link.configure({ openOnClick: false }),
      TextAlign.configure({ types: ["heading", "paragraph"] }),
      Underline,
      Placeholder.configure({ placeholder: "Začněte psát obsah šablony..." }),
    ],
    content: language === "sk" ? contentSk : content,
    onUpdate: ({ editor }) => {
      const html = editor.getHTML();
      if (language === "sk") {
        setContentSk(html);
      } else {
        setContent(html);
      }
    },
  });

  // Update editor when language changes
  useEffect(() => {
    if (editor) {
      const newContent = language === "sk" ? contentSk : content;
      if (editor.getHTML() !== newContent) {
        editor.commands.setContent(newContent || "");
      }
    }
  }, [language, editor]);

  // Generate preview
  const previewMutation = useMutation({
    mutationFn: (data: { content: string; lang: string }) =>
      api.post<ApiResponse<{ subject?: string; content: string }>>(`/api/admin/templates/${id}/preview`, {
        lang: data.lang,
      }),
    onSuccess: (data) => {
      setPreviewHtml(data.data?.content || "");
    },
  });

  // Update preview when content changes
  useEffect(() => {
    if (!isNew && id) {
      const currentContent = language === "sk" ? contentSk : content;
      // For preview, just replace variables with sample data locally
      let preview = currentContent;
      // Simple local preview with placeholder replacement
      const testData: Record<string, string> = {
        firstName: "Jan",
        lastName: "Novák",
        tripName: "Objevte krásy Peru",
        destination: "Peru",
        price: "89 900 Kč",
        orderNumber: "ORD-2025-0042",
        companyName: "CK Go2",
      };
      for (const [key, value] of Object.entries(testData)) {
        preview = preview.replace(new RegExp(`\\{\\{${key}\\}\\}`, "g"), value);
      }
      setPreviewHtml(preview);
    }
  }, [content, contentSk, language, id, isNew]);

  // Save mutation
  const saveMutation = useMutation({
    mutationFn: (data: Partial<Template>) =>
      isNew
        ? api.post<ApiResponse<Template>>("/api/admin/templates", data)
        : api.put<ApiResponse<Template>>(`/api/admin/templates/${id}`, data),
    onSuccess: (data) => {
      toast.success(isNew ? "Šablona vytvořena" : "Šablona uložena");
      queryClient.invalidateQueries({ queryKey: ["templates"] });
      if (isNew && data.data?.id) {
        navigate(`/templates/${data.data.id}`);
      }
    },
    onError: (error: Error) => {
      toast.error(error.message || "Nepodařilo se uložit šablonu");
    },
  });

  const handleSave = () => {
    if (!name.trim()) {
      toast.error("Zadejte název šablony");
      return;
    }
    if (!content.trim()) {
      toast.error("Zadejte obsah šablony");
      return;
    }

    saveMutation.mutate({
      name,
      description: description || undefined,
      type,
      category,
      subject: type === "email" ? subject : undefined,
      subjectSk: type === "email" ? subjectSk : undefined,
      content,
      contentSk: contentSk || undefined,
      isActive,
    });
  };

  // Insert variable at cursor
  const insertVariable = useCallback(
    (variable: string) => {
      const tag = `{{${variable}}}`;
      if (editorMode === "wysiwyg" && editor) {
        editor.commands.insertContent(tag);
      } else {
        // For HTML mode, we'd need a ref to textarea
        if (language === "sk") {
          setContentSk((prev) => prev + tag);
        } else {
          setContent((prev) => prev + tag);
        }
      }
      setShowVariables(false);
    },
    [editor, editorMode, language]
  );

  if (isLoading) {
    return (
      <div className="flex h-96 items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary-500" />
      </div>
    );
  }

  return (
    <div className="h-full">
      {/* Header */}
      <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-4">
          <button onClick={() => navigate("/templates")} className="btn-ghost">
            <ArrowLeft className="h-5 w-5" />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
              {isNew ? "Nová šablona" : name || "Upravit šablonu"}
            </h1>
            {!isNew && templateData?.data?.versionCount && (
              <p className="text-sm text-gray-500">
                Verze: {templateData.data.versionCount}
              </p>
            )}
          </div>
        </div>

        <div className="flex items-center gap-2">
          {!isNew && (
            <>
              <button
                onClick={() => setShowHistory(true)}
                className="btn-ghost"
                title="Historie verzí"
              >
                <History className="h-5 w-5" />
              </button>
              <button
                onClick={() => setShowAiModal(true)}
                className="btn-ghost"
                title="AI generování"
              >
                <Sparkles className="h-5 w-5" />
              </button>
            </>
          )}
          <button
            onClick={handleSave}
            disabled={saveMutation.isPending}
            className="btn-primary"
          >
            {saveMutation.isPending ? (
              <Loader2 className="h-5 w-5 animate-spin" />
            ) : (
              <Save className="h-5 w-5" />
            )}
            Uložit
          </button>
        </div>
      </div>

      {/* Main content */}
      <div className="grid gap-6 lg:grid-cols-2">
        {/* Left panel - Editor */}
        <div className="space-y-4">
          {/* Basic info */}
          <div className="card space-y-4">
            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <label className="label">Název *</label>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Název šablony"
                  className="input"
                  required
                />
              </div>
              <div>
                <label className="label">Popis</label>
                <input
                  type="text"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="Volitelný popis"
                  className="input"
                />
              </div>
            </div>

            <div className="grid gap-4 sm:grid-cols-3">
              <div>
                <label className="label">Typ *</label>
                <select
                  value={type}
                  onChange={(e) => setType(e.target.value as "email" | "pdf")}
                  className="input"
                  disabled={!isNew}
                >
                  {TYPES.map((t) => (
                    <option key={t.value} value={t.value}>
                      {t.label}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="label">Kategorie *</label>
                <select
                  value={category}
                  onChange={(e) => setCategory(e.target.value)}
                  className="input"
                >
                  {CATEGORIES.map((c) => (
                    <option key={c.value} value={c.value}>
                      {c.label}
                    </option>
                  ))}
                </select>
              </div>
              <div className="flex items-end">
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={isActive}
                    onChange={(e) => setIsActive(e.target.checked)}
                    className="h-4 w-4 rounded text-primary-600"
                  />
                  <span className="text-gray-700 dark:text-gray-300">
                    Aktivní
                  </span>
                </label>
              </div>
            </div>

            {/* Subject for emails */}
            {type === "email" && (
              <div>
                <label className="label">
                  Předmět {language === "sk" ? "(SK)" : "(CZ)"}
                </label>
                <input
                  type="text"
                  value={language === "sk" ? subjectSk : subject}
                  onChange={(e) =>
                    language === "sk"
                      ? setSubjectSk(e.target.value)
                      : setSubject(e.target.value)
                  }
                  placeholder="Předmět emailu"
                  className="input"
                />
              </div>
            )}
          </div>

          {/* Editor toolbar */}
          <div className="card">
            <div className="mb-4 flex flex-wrap items-center justify-between gap-2 border-b border-gray-200 pb-4 dark:border-gray-700">
              {/* Mode toggle */}
              <div className="flex items-center rounded-lg border border-gray-200 dark:border-gray-700">
                <button
                  onClick={() => setEditorMode("wysiwyg")}
                  className={clsx(
                    "flex items-center gap-1 rounded-l-lg px-3 py-1.5 text-sm transition-colors",
                    editorMode === "wysiwyg"
                      ? "bg-primary-100 text-primary-700 dark:bg-primary-900/30 dark:text-primary-400"
                      : "text-gray-600 hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-gray-800"
                  )}
                >
                  <Type className="h-4 w-4" />
                  WYSIWYG
                </button>
                <button
                  onClick={() => setEditorMode("html")}
                  className={clsx(
                    "flex items-center gap-1 rounded-r-lg px-3 py-1.5 text-sm transition-colors",
                    editorMode === "html"
                      ? "bg-primary-100 text-primary-700 dark:bg-primary-900/30 dark:text-primary-400"
                      : "text-gray-600 hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-gray-800"
                  )}
                >
                  <Code className="h-4 w-4" />
                  HTML
                </button>
              </div>

              {/* Language toggle */}
              <div className="flex items-center gap-2">
                <Globe className="h-4 w-4 text-gray-400" />
                <div className="flex items-center rounded-lg border border-gray-200 dark:border-gray-700">
                  <button
                    onClick={() => setLanguage("cs")}
                    className={clsx(
                      "rounded-l-lg px-3 py-1.5 text-sm transition-colors",
                      language === "cs"
                        ? "bg-primary-100 text-primary-700 dark:bg-primary-900/30 dark:text-primary-400"
                        : "text-gray-600 hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-gray-800"
                    )}
                  >
                    CZ
                  </button>
                  <button
                    onClick={() => setLanguage("sk")}
                    className={clsx(
                      "rounded-r-lg px-3 py-1.5 text-sm transition-colors",
                      language === "sk"
                        ? "bg-primary-100 text-primary-700 dark:bg-primary-900/30 dark:text-primary-400"
                        : "text-gray-600 hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-gray-800"
                    )}
                  >
                    SK
                  </button>
                </div>
              </div>

              {/* Variables dropdown */}
              <div className="relative">
                <button
                  onClick={() => setShowVariables(!showVariables)}
                  className="btn-ghost btn-sm"
                >
                  <Variable className="h-4 w-4" />
                  Proměnné
                  <ChevronDown className="h-4 w-4" />
                </button>

                {showVariables && (
                  <>
                    <div
                      className="fixed inset-0 z-40"
                      onClick={() => setShowVariables(false)}
                    />
                    <div className="absolute right-0 z-50 mt-2 w-72 max-h-96 overflow-auto rounded-lg border border-gray-200 bg-white shadow-lg dark:border-gray-700 dark:bg-gray-800">
                      {Object.entries(variables).map(([group, vars]) => (
                        <div key={group}>
                          <div className="sticky top-0 bg-gray-50 px-4 py-2 text-xs font-semibold uppercase text-gray-500 dark:bg-gray-900 dark:text-gray-400">
                            {group === "customer" && "Zákazník"}
                            {group === "trip" && "Zájezd"}
                            {group === "order" && "Objednávka"}
                            {group === "guide" && "Průvodce"}
                            {group === "company" && "Firma"}
                            {group === "voucher" && "Voucher"}
                          </div>
                          {vars.map((v) => (
                            <button
                              key={v.key}
                              onClick={() => insertVariable(v.key)}
                              className="block w-full px-4 py-2 text-left text-sm hover:bg-gray-100 dark:hover:bg-gray-700"
                            >
                              <span className="font-mono text-primary-600">
                                {`{{${v.key}}}`}
                              </span>
                              <span className="ml-2 text-gray-500">{v.label}</span>
                            </button>
                          ))}
                        </div>
                      ))}
                    </div>
                  </>
                )}
              </div>
            </div>

            {/* WYSIWYG toolbar */}
            {editorMode === "wysiwyg" && editor && (
              <div className="mb-4 flex flex-wrap gap-1 border-b border-gray-200 pb-4 dark:border-gray-700">
                <button
                  onClick={() => editor.chain().focus().toggleBold().run()}
                  className={clsx(
                    "rounded p-2 hover:bg-gray-100 dark:hover:bg-gray-700",
                    editor.isActive("bold") && "bg-gray-200 dark:bg-gray-700"
                  )}
                >
                  <Bold className="h-4 w-4" />
                </button>
                <button
                  onClick={() => editor.chain().focus().toggleItalic().run()}
                  className={clsx(
                    "rounded p-2 hover:bg-gray-100 dark:hover:bg-gray-700",
                    editor.isActive("italic") && "bg-gray-200 dark:bg-gray-700"
                  )}
                >
                  <Italic className="h-4 w-4" />
                </button>
                <button
                  onClick={() => editor.chain().focus().toggleUnderline().run()}
                  className={clsx(
                    "rounded p-2 hover:bg-gray-100 dark:hover:bg-gray-700",
                    editor.isActive("underline") && "bg-gray-200 dark:bg-gray-700"
                  )}
                >
                  <UnderlineIcon className="h-4 w-4" />
                </button>
                <div className="mx-2 w-px bg-gray-200 dark:bg-gray-700" />
                <button
                  onClick={() => editor.chain().focus().setTextAlign("left").run()}
                  className={clsx(
                    "rounded p-2 hover:bg-gray-100 dark:hover:bg-gray-700",
                    editor.isActive({ textAlign: "left" }) && "bg-gray-200 dark:bg-gray-700"
                  )}
                >
                  <AlignLeft className="h-4 w-4" />
                </button>
                <button
                  onClick={() => editor.chain().focus().setTextAlign("center").run()}
                  className={clsx(
                    "rounded p-2 hover:bg-gray-100 dark:hover:bg-gray-700",
                    editor.isActive({ textAlign: "center" }) && "bg-gray-200 dark:bg-gray-700"
                  )}
                >
                  <AlignCenter className="h-4 w-4" />
                </button>
                <button
                  onClick={() => editor.chain().focus().setTextAlign("right").run()}
                  className={clsx(
                    "rounded p-2 hover:bg-gray-100 dark:hover:bg-gray-700",
                    editor.isActive({ textAlign: "right" }) && "bg-gray-200 dark:bg-gray-700"
                  )}
                >
                  <AlignRight className="h-4 w-4" />
                </button>
                <div className="mx-2 w-px bg-gray-200 dark:bg-gray-700" />
                <button
                  onClick={() => editor.chain().focus().toggleBulletList().run()}
                  className={clsx(
                    "rounded p-2 hover:bg-gray-100 dark:hover:bg-gray-700",
                    editor.isActive("bulletList") && "bg-gray-200 dark:bg-gray-700"
                  )}
                >
                  <List className="h-4 w-4" />
                </button>
                <button
                  onClick={() => editor.chain().focus().toggleOrderedList().run()}
                  className={clsx(
                    "rounded p-2 hover:bg-gray-100 dark:hover:bg-gray-700",
                    editor.isActive("orderedList") && "bg-gray-200 dark:bg-gray-700"
                  )}
                >
                  <ListOrdered className="h-4 w-4" />
                </button>
                <div className="mx-2 w-px bg-gray-200 dark:bg-gray-700" />
                <button
                  onClick={() => editor.chain().focus().undo().run()}
                  disabled={!editor.can().undo()}
                  className="rounded p-2 hover:bg-gray-100 disabled:opacity-50 dark:hover:bg-gray-700"
                >
                  <Undo className="h-4 w-4" />
                </button>
                <button
                  onClick={() => editor.chain().focus().redo().run()}
                  disabled={!editor.can().redo()}
                  className="rounded p-2 hover:bg-gray-100 disabled:opacity-50 dark:hover:bg-gray-700"
                >
                  <Redo className="h-4 w-4" />
                </button>
              </div>
            )}

            {/* Editor content */}
            {editorMode === "wysiwyg" ? (
              <EditorContent
                editor={editor}
                className="prose prose-sm dark:prose-invert max-w-none min-h-[400px] rounded-lg border border-gray-200 p-4 focus-within:border-primary-500 focus-within:ring-2 focus-within:ring-primary-500/20 dark:border-gray-700"
              />
            ) : (
              <textarea
                value={language === "sk" ? contentSk : content}
                onChange={(e) =>
                  language === "sk"
                    ? setContentSk(e.target.value)
                    : setContent(e.target.value)
                }
                placeholder="<html>...</html>"
                className="input min-h-[400px] font-mono text-sm"
              />
            )}
          </div>
        </div>

        {/* Right panel - Preview */}
        <div className="space-y-4">
          <div className="card">
            <div className="mb-4 flex items-center justify-between">
              <h3 className="font-semibold text-gray-900 dark:text-white">
                <Eye className="mr-2 inline h-5 w-5" />
                Náhled {language === "sk" ? "(SK)" : "(CZ)"}
              </h3>
            </div>

            {/* Preview iframe */}
            <div className="rounded-lg border border-gray-200 bg-white dark:border-gray-700">
              <iframe
                srcDoc={`
                  <!DOCTYPE html>
                  <html>
                    <head>
                      <meta charset="UTF-8">
                      <style>
                        body { font-family: system-ui, sans-serif; padding: 20px; margin: 0; }
                        * { box-sizing: border-box; }
                      </style>
                    </head>
                    <body>${previewHtml || '<p style="color: #999;">Náhled se zobrazí zde...</p>'}</body>
                  </html>
                `}
                className="h-[500px] w-full rounded-lg"
                title="Template Preview"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Version History Modal */}
      {showHistory && !isNew && (
        <VersionHistoryModal
          templateId={id!}
          onClose={() => setShowHistory(false)}
          onRestore={() => {
            queryClient.invalidateQueries({ queryKey: ["template", id] });
            setShowHistory(false);
          }}
        />
      )}

      {/* AI Generator Modal */}
      {showAiModal && (
        <AIGeneratorModal
          type={type}
          category={category}
          onClose={() => setShowAiModal(false)}
          onUse={(generated) => {
            if (generated.subject) {
              setSubject(generated.subject);
            }
            setContent(generated.content);
            if (editor) {
              editor.commands.setContent(generated.content);
            }
            setShowAiModal(false);
          }}
        />
      )}
    </div>
  );
}

// Version History Modal
function VersionHistoryModal({
  templateId,
  onClose,
  onRestore,
}: {
  templateId: string;
  onClose: () => void;
  onRestore: () => void;
}) {
  const { data: versionsData, isLoading } = useQuery({
    queryKey: ["template-versions", templateId],
    queryFn: () =>
      api.get<ApiResponse<TemplateVersion[]>>(
        `/api/admin/templates/${templateId}/versions`
      ),
  });

  const restoreMutation = useMutation({
    mutationFn: (versionId: string) =>
      api.post(`/api/admin/templates/${templateId}/restore/${versionId}`),
    onSuccess: () => {
      toast.success("Verze obnovena");
      onRestore();
    },
    onError: (error: Error) => {
      toast.error(error.message || "Nepodařilo se obnovit verzi");
    },
  });

  const versions = versionsData?.data || [];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative z-10 mx-4 max-h-[80vh] w-full max-w-2xl overflow-y-auto rounded-xl bg-white p-6 shadow-2xl dark:bg-gray-800">
        <h2 className="mb-4 text-xl font-bold text-gray-900 dark:text-white">
          <History className="mr-2 inline h-6 w-6" />
          Historie verzí
        </h2>

        {isLoading ? (
          <div className="flex h-32 items-center justify-center">
            <Loader2 className="h-8 w-8 animate-spin text-primary-500" />
          </div>
        ) : versions.length === 0 ? (
          <p className="text-gray-500">Žádné verze</p>
        ) : (
          <div className="space-y-3">
            {versions.map((v, index) => (
              <div
                key={v.id}
                className="flex items-center justify-between rounded-lg border border-gray-200 p-4 dark:border-gray-700"
              >
                <div>
                  <div className="flex items-center gap-2">
                    <span className="font-semibold">Verze {v.version}</span>
                    {index === 0 && (
                      <span className="rounded bg-primary-100 px-2 py-0.5 text-xs text-primary-700 dark:bg-primary-900/30 dark:text-primary-400">
                        Aktuální
                      </span>
                    )}
                  </div>
                  <p className="mt-1 text-sm text-gray-500">
                    {v.changeNote || "Bez poznámky"}
                  </p>
                  <p className="text-xs text-gray-400">
                    {new Date(v.createdAt).toLocaleString("cs-CZ")}
                  </p>
                </div>
                {index > 0 && (
                  <button
                    onClick={() => restoreMutation.mutate(v.id)}
                    disabled={restoreMutation.isPending}
                    className="btn-ghost btn-sm"
                  >
                    Obnovit
                  </button>
                )}
              </div>
            ))}
          </div>
        )}

        <div className="mt-6 flex justify-end">
          <button onClick={onClose} className="btn-ghost">
            Zavřít
          </button>
        </div>
      </div>
    </div>
  );
}

// AI Generator Modal
function AIGeneratorModal({
  type,
  category,
  onClose,
  onUse,
}: {
  type: "email" | "pdf";
  category: string;
  onClose: () => void;
  onUse: (data: { subject?: string; content: string }) => void;
}) {
  const [prompt, setPrompt] = useState("");
  const [tone, setTone] = useState<"formal" | "friendly" | "excited">("friendly");
  const [generatedContent, setGeneratedContent] = useState<{
    subject?: string;
    content: string;
  } | null>(null);

  const generateMutation = useMutation({
    mutationFn: () =>
      api.post<ApiResponse<{ subject?: string; content: string }>>(
        "/api/admin/templates/generate",
        { prompt, type, category, tone }
      ),
    onSuccess: (data) => {
      setGeneratedContent(data.data || null);
    },
    onError: (error: Error) => {
      toast.error(error.message || "Nepodařilo se vygenerovat obsah");
    },
  });

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative z-10 mx-4 max-h-[90vh] w-full max-w-3xl overflow-y-auto rounded-xl bg-white p-6 shadow-2xl dark:bg-gray-800">
        <h2 className="mb-4 text-xl font-bold text-gray-900 dark:text-white">
          <Sparkles className="mr-2 inline h-6 w-6 text-primary-500" />
          AI Generování obsahu
        </h2>

        {!generatedContent ? (
          <div className="space-y-4">
            <div>
              <label className="label">Zadejte popis šablony</label>
              <textarea
                value={prompt}
                onChange={(e) => setPrompt(e.target.value)}
                placeholder="Např.: Vytvořte email pro potvrzení rezervace zájezdu do Peru s informacemi o platbě zálohy."
                className="input min-h-[100px]"
              />
            </div>

            <div>
              <label className="label">Tón komunikace</label>
              <select
                value={tone}
                onChange={(e) => setTone(e.target.value as typeof tone)}
                className="input"
              >
                <option value="formal">Formální</option>
                <option value="friendly">Přátelský</option>
                <option value="excited">Nadšený</option>
              </select>
            </div>

            <div className="flex justify-end gap-2">
              <button onClick={onClose} className="btn-ghost">
                Zrušit
              </button>
              <button
                onClick={() => generateMutation.mutate()}
                disabled={!prompt.trim() || generateMutation.isPending}
                className="btn-primary"
              >
                {generateMutation.isPending ? (
                  <Loader2 className="h-5 w-5 animate-spin" />
                ) : (
                  <Sparkles className="h-5 w-5" />
                )}
                Generovat
              </button>
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            {generatedContent.subject && (
              <div>
                <label className="label">Vygenerovaný předmět</label>
                <input
                  type="text"
                  value={generatedContent.subject}
                  readOnly
                  className="input bg-gray-50"
                />
              </div>
            )}

            <div>
              <label className="label">Vygenerovaný obsah</label>
              <div className="rounded-lg border border-gray-200 bg-gray-50 p-4 dark:border-gray-700 dark:bg-gray-900">
                <div
                  className="prose prose-sm dark:prose-invert max-w-none"
                  dangerouslySetInnerHTML={{ __html: generatedContent.content }}
                />
              </div>
            </div>

            <div className="flex justify-end gap-2">
              <button
                onClick={() => setGeneratedContent(null)}
                className="btn-ghost"
              >
                Zkusit znovu
              </button>
              <button onClick={onClose} className="btn-ghost">
                Zrušit
              </button>
              <button
                onClick={() => onUse(generatedContent)}
                className="btn-primary"
              >
                Použít
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
